import ReconnectingWebSocket from "reconnecting-websocket";
import { Signal } from "signals";
import { Connector } from "slotworks/server/connectors/connector";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import Logger from "slotworks/utils/logger";

export class SocketConnector implements Connector {

    protected webSocket: WebSocket | ReconnectingWebSocket;

    protected errorSignal: Signal = new Signal();

    protected requestResolve: (value: any) => void;
    protected openResolve: (value: any) => void;

    public sendRequest(request: SocketRequest): Contract<{}> {
        const actions = [];

        if (!this.webSocket) {
            actions.push(() => new Contract<void>((resolve) => {
                try {
                    this.webSocket = new ReconnectingWebSocket(request.url);
                } catch (e) {
                    Logger.warn("Reconnecting web socket failed, falling back to native");
                    this.webSocket = new WebSocket(request.url);
                }
                (this.webSocket as any).addEventListener("error", this.onErrorListener);
                (this.webSocket as any).addEventListener("message", this.onMessage);
                (this.webSocket as any).addEventListener("open", this.onOpen);
                (this.webSocket as any).addEventListener("close", this.onClose);

                // Close socket connection on window close.
                window.onbeforeunload = () => {
                    this.close();
                };

                if (this.webSocket.readyState === WebSocket.OPEN) {
                    resolve(null);
                } else {
                    this.openResolve = resolve;
                }
            }));
        }

        actions.push(() => new Contract((resolve) => {
            this.requestResolve = resolve;
            this.webSocket.send(JSON.stringify(request.body));
        }));

        return new Sequence(actions);
    }

    public onError(method: (message: string, fatal: boolean) => void) {
        this.errorSignal.add(method);
    }

    public close(): void {
        (this.webSocket as any).removeEventListener("error", this.onErrorListener);
        (this.webSocket as any).removeEventListener("message", this.onMessage);
        (this.webSocket as any).removeEventListener("open", this.onOpen);
        (this.webSocket as any).removeEventListener("close", this.onClose);

        this.webSocket.close();
    }

    protected onConnect() {
        if (this.openResolve) {
            this.openResolve(null);
            this.openResolve = null;
        } else {
            Logger.log("SocketConnector: Websocket Reconnected.");
        }
    }

    protected onDisconnect() {
        Logger.log("SocketConnector: Websocket Disconnected.");
        this.close();
        this.errorSignal.dispatch("err.dis", true);
    }

    protected resolveRequest(message: any) {
        if (this.requestResolve) {
            this.requestResolve(message);
            this.requestResolve = null;
        }
    }

    private onErrorListener = (error: ErrorEvent) => {
        this.close();
        this.errorSignal.dispatch("err.dis", true);
    }

    private onMessage = (response: any) => {
        this.resolveRequest(response);
    }

    private onOpen = () => {
        this.onConnect();
    }

    private onClose = () => {
        this.onDisconnect();
    }
}

export class SocketRequest {
    constructor(public body: any, public url: string = "") {
    }
}

export default new SocketConnector();
